import { Icon, IconButton, IconNames, Text } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import {
  PropsWithChildren,
  ReactElement,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';

import { useBreakpointValue } from 'utils/mediaQuery';

import styles from './header.module.scss';

type HeaderProps = PropsWithChildren<{
  iconsList: ReactNode;
  isOpen: boolean;
  isFavorite: boolean;
  avatar: ReactNode;
  onFavoriteClick: () => void;
  onToggle: (e: ReactMouseEvent<HTMLElement>) => void;
  status: string;
  onPhone?: boolean;
  simultaneous?: boolean;
  inAcw?: boolean;
  actions?: ReactElement;
}>;

export const Header = ({
  children,
  iconsList,
  isOpen,
  isFavorite,
  avatar,
  onFavoriteClick,
  onToggle,
  status,
  onPhone,
  simultaneous,
  inAcw,
  actions,
}: HeaderProps): JSX.Element => {
  const isSmallScreen = useBreakpointValue({ base: true, lg: false });

  const rootClass = classnames(styles.headerTeam, {
    [styles.headerTeamOpen]: isOpen,
  });

  const statusClass = classnames(styles.status, {
    [styles.statusActive]: status === 'working',
    [styles.statusAway]: status === 'away',
    [styles.statusOffline]: status === 'offline',
  });

  const iconChannelClass = classnames(styles.statusIcon, {
    [styles.statusIconActive]: status === 'working',
    [styles.statusIconAway]: status === 'away',
    [styles.statusIconOffline]: status === 'offline',
  });

  let iconChannel = '';
  if (onPhone === true) {
    iconChannel = 'phone';
  } else if (simultaneous === true) {
    iconChannel = 'simultaneous';
  } else if (inAcw === true) {
    iconChannel = 'circle-z';
  }

  const handleFavoriteToggle = (
    event: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    onFavoriteClick?.();
  };

  const showStatus =
    status === 'away' || status === 'working' || status === 'offline';
  const showIcon = !!(onPhone || simultaneous || inAcw);

  return (
    <div
      className={rootClass}
      onClick={onToggle}
      // prevent selection of contents if doubleclicking
      onMouseDown={(event) => event.preventDefault()}
    >
      <div className={styles.headerPerson}>
        {isFavorite !== undefined && (
          <IconButton
            className={styles.favoriteIcon}
            variant="tertiary"
            icon={isFavorite ? 'star-full' : 'star'}
            iconProps={{
              color: isFavorite ? colors.orange[300] : colors.neutral[400],
            }}
            onClick={handleFavoriteToggle}
          />
        )}
        {!!avatar && <div className={styles.avatarWrap}>{avatar}</div>}
        {showStatus && !showIcon && <div className={statusClass} />}
        {showIcon && (
          <Icon name={iconChannel as IconNames} className={iconChannelClass} />
        )}
        <Text.Paragraph
          as="div"
          variant="2"
          onMouseDown={(event) => event.stopPropagation()}
          className={styles.title}
        >
          {children}
        </Text.Paragraph>
      </div>

      <div className={styles.pullRight}>
        {!isSmallScreen || !iconsList ? (
          <div className={styles.icons}>{iconsList}</div>
        ) : null}

        {actions}

        <Icon
          name="angle-right"
          color="secondary"
          size={24}
          className={styles.arrow}
        />
      </div>
    </div>
  );
};

Header.displayName = 'Header';
